import React from "react"
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  MenuItemOption,
  MenuOptionGroup,
  VStack,
} from "@chakra-ui/react"
import { DioboxEvent } from "sharedTypes"
import Icon from "components/elements/Icon"
import Spinner from "components/elements/Spinner"
import Menu from "components/dialogs/Menu"
import PreloadImages from "components/elements/PreloadImages"
import useResolvedTranslations from "pages/EventShow/Settings/LanguageTemplates/useResolvedTranslations"
import KioskPreviewDevice from "./components/KioskPreviewDevice"
import {
  IPAD_PORTRAIT_PREVIEW_WIDTH,
  IPAD_PORTRAIT_PREVIEW_HEIGHT,
  IPAD_PRO_11_M4_LANDSCAPE,
  IPAD_PRO_11_M4_PORTRAIT,
  IPHONE_16_PRO_BLACK_TITANIUM,
} from "./kioskDeviceSpecs"
import {
  previewDeviceLabel,
  previewScreenLabel,
  type PreviewDevice,
  type PreviewScreen,
} from "./kioskPreviewTypes"

const KIOSK_FRAME_SRCS = [
  IPHONE_16_PRO_BLACK_TITANIUM.frameSrc,
  IPAD_PRO_11_M4_PORTRAIT.frameSrc,
  IPAD_PRO_11_M4_LANDSCAPE.frameSrc,
]

const PLACEHOLDER_W = IPAD_PORTRAIT_PREVIEW_WIDTH
const PLACEHOLDER_H = IPAD_PORTRAIT_PREVIEW_HEIGHT

type Props = {
  event: DioboxEvent
  onOpenBackground: () => void
  onOpenBrandLogo: () => void
  onOpenEventTitle: () => void
  onOpenColorTheme: () => void
  onOpenGuestDisplay: () => void
  onOpenOnScreenText: () => void
  /** When omitted, "Kiosk Actions" is hidden (e.g. `kiosk_forms` feature flag off). */
  onOpenActions?: () => void
}

const KioskPreviewCard = ({
  event,
  onOpenBackground,
  onOpenBrandLogo,
  onOpenEventTitle,
  onOpenColorTheme,
  onOpenGuestDisplay,
  onOpenOnScreenText,
  onOpenActions,
}: Props) => {
  const [screen, setScreen] = React.useState<PreviewScreen>("home")
  const [device, setDevice] = React.useState<PreviewDevice>("ipadPortrait")
  const [framesReady, setFramesReady] = React.useState(false)

  const resolvedTranslations = useResolvedTranslations()

  const accent = event.kioskAccentColor || "#4A90E2"
  // Title is shown when the kiosk toggle is on and there's a title to show; otherwise it's
  // hidden and the logo expands to fill the same band (iOS `kiosk_show_event_title`).
  const showTitle = event.kioskShowEventTitle && Boolean(event.publicEventTitle)
  const title = event.publicEventTitle || "Event"
  const scanLabel = resolvedTranslations.kioskScanQrCode || "Scan QR Code"
  const registerLabel = resolvedTranslations.kioskRegister || "Register"
  const backgroundUrl = event.kioskBackgroundImageUrl || ""
  const logoUrl = event.kioskLogoUrl || ""

  const previewProps = {
    screen,
    accent,
    title,
    showTitle,
    scanLabel,
    registerLabel,
    scanQrCodeEnabled: Boolean(event.kioskScanQrCodeEnabled),
    walkInRegistrationEnabled: Boolean(event.kioskWalkInRegistrationEnabled),
    backgroundUrl,
    logoUrl,
  }

  return (
    <Box borderWidth={1} borderColor="gray.200" bg="white" borderRadius="xl" overflow="hidden">
      <HStack px={3} py={1} bg="gray.50" borderBottomWidth={1} borderColor="gray.100" justify="space-between">
        <Menu
          noPadding
          placement="bottom-start"
          buttonText={previewScreenLabel[screen]}
          buttonVariant="ghost"
          buttonColor="black"
          menuItems={[
            <MenuOptionGroup
              key="screen-options"
              defaultValue={screen}
              type="radio"
              onChange={(value) => setScreen(value as PreviewScreen)}
            >
              {Object.entries(previewScreenLabel).map(([value, label]) => (
                <MenuItemOption key={value} value={value} py={3}>
                  {label}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>,
          ]}
        />

        <Menu
          noPadding
          placement="bottom-end"
          buttonText={previewDeviceLabel[device]}
          buttonVariant="ghost"
          buttonColor="black"
          menuItems={[
            <MenuOptionGroup
              key="device-options"
              defaultValue={device}
              type="radio"
              onChange={(value) => setDevice(value as PreviewDevice)}
            >
              {Object.entries(previewDeviceLabel).map(([value, label]) => (
                <MenuItemOption key={value} value={value} py={3}>
                  {label}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>,
          ]}
        />
      </HStack>

      <Flex p={8} gap={8} align="center" justify="center" bg="white">
        <Box flexShrink={0}>
          {!framesReady ? (
            <Center w={PLACEHOLDER_W} h={PLACEHOLDER_H}>
              <Spinner size="xl" heightFull />
            </Center>
          ) : (
            <KioskPreviewDevice device={device} {...previewProps} />
          )}
        </Box>

        <Box
          w="260px"
          display={{ base: "none", md: "block" }}
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="xl"
          bg="white"
          overflow="hidden"
          boxShadow="0 1px 3px rgba(0, 0, 0, 0.06)"
        >
          <Flex align="center" gap={3} px={4} py={3} bgColor="gray.50" borderBottomWidth={1} borderColor="gray.100">
            <Icon icon="settings" size={5} color="gray.600" />
            <Box fontWeight="600" fontSize="md" color="gray.800" letterSpacing="tight">
              Customize
            </Box>
          </Flex>
          <VStack align="stretch" spacing={0}>
            {(
              [
                { label: "Background", onClick: onOpenBackground },
                { label: "Brand Logo", onClick: onOpenBrandLogo },
                { label: "Event Title", onClick: onOpenEventTitle },
                { label: "Color Theme", onClick: onOpenColorTheme },
                { label: "Display Settings", onClick: onOpenGuestDisplay },
                { label: "On-Screen Text", onClick: onOpenOnScreenText },
                ...(onOpenActions ? [{ label: "Kiosk Actions", onClick: onOpenActions }] : []),
              ] satisfies Array<{ label: string; onClick: () => void }>
            ).map(({ label, onClick }) => (
              <Button
                key={label}
                variant="ghost"
                size="sm"
                fontSize="sm"
                w="100%"
                justifyContent="flex-start"
                onClick={onClick}
                color="gray.700"
                fontWeight="semibold"
                py={6}
                px={4}
                borderRadius="md"
                _hover={{
                  bg: "gray.50",
                }}
                _active={{
                  bg: "gray.100",
                }}
                transition="all 0.15s ease"
              >
                {label}
              </Button>
            ))}
          </VStack>
        </Box>
      </Flex>
      <PreloadImages srcs={KIOSK_FRAME_SRCS} onReady={() => setFramesReady(true)} />
    </Box>
  )
}

export default KioskPreviewCard
